import { z } from 'zod';

export const FILTER_SET_DEFAULT_NAME = 'default';

export const FilterDefinitionUsageType = z
  .enum(['primary', 'secondary', 'normal'])
  .describe('FilterDefinitionUsageType');

export const FilterDefinitionUsageTypes = Object.values(
  FilterDefinitionUsageType.Values,
);

export const FilterDefinitionDisplayType = z
  .enum([
    /**
     * These two types support the cross-walking behavior driven by dependsOn
     */
    'select_single',
    'select_multi',

    /**
     * The rest of these are the standard filter types
     */
    'range_scalar',
    'range_date',
  ])
  .describe('FilterDefinitionDisplayType');

export type FilterDefinitionDisplayType = z.infer<
  typeof FilterDefinitionDisplayType
>;

export const FilterDefinitionDisplayTypes = Object.values(
  FilterDefinitionDisplayType.Values,
);

// If we don't have one, we default to select_multi to mirror legacy behavior
export const FILTER_DEFINITION_DEFAULT_DISPLAY_TYPE = 'select_multi';

export const FilterDefinition = z
  .object({
    columnHeaderValue: z.string(),
    displayName: z.string(),
    dependsOn: z.array(z.string()).optional(),
    usageType: FilterDefinitionUsageType.nullish(),

    /**
     * The type of filter (ie: checkboxes, range_date, range_scalar, etc)
     */
    displayType: FilterDefinitionDisplayType.optional(),

    /**
     * The precision of the scalar filter
     *
     * This should be a number of decimal places to display. For example, 1 means values like 20.1, 20.2, 20.3, etc.
     */
    displayTypePrecision: z.number().optional(),
  })
  .describe('FilterDefinition');

export type FilterDefinition = z.infer<typeof FilterDefinition>;

export const FilterSetFilterDefinitions = z
  .array(FilterDefinition)
  .describe('FilterSetFilterDefinitions');

export type FilterSetFilterDefinitions = z.infer<
  typeof FilterSetFilterDefinitions
>;

export const FilterSetDefinition = z
  .object({
    filterSetName: z.string(),
    filterDefinitions: FilterSetFilterDefinitions,
  })
  .describe('FilterSetDefinition');

export type FilterSetDefinition = z.infer<typeof FilterSetDefinition>;

export const DisplayValuesMap = z
  .record(z.string(), z.string())
  .describe('DisplayValuesMap');

export type DisplayValuesMap = z.infer<typeof DisplayValuesMap>;

/**
 * For dynamic filters, we need to know the unique values for a given column so we can render the dropdown options.
 */
export const FilterDefinitionWithValues = FilterDefinition.extend({
  uniqueValues: z.array(z.string()),
  // In some cases, we allow the dimensions to be used as a filter, and when we do this then the "value" would actually be incomprehensible to the user.
  // This map is provided when we want to display a more user-friendly value for the filter.
  optionalDisplayValues: DisplayValuesMap.optional(),
}).describe('FilterDefinitionWithValues');

export type FilterDefinitionWithValues = z.infer<
  typeof FilterDefinitionWithValues
>;

export const FilterSetDefinitionWithValues = FilterSetDefinition.extend({
  filterDefinitions: z.array(FilterDefinitionWithValues),
}).describe('FilterSetDefinitionWithValues');

export type FilterSetDefinitionWithValues = z.infer<
  typeof FilterSetDefinitionWithValues
>;

export const FilterSetDefinitions = z
  .array(FilterSetDefinition)
  .describe('FilterSetDefinitions');

export type FilterSetDefinitions = z.infer<typeof FilterSetDefinitions>;

export const FilterSetDefinitionsWithDefaultFilters = z
  .object({
    defaultFilters: FilterSetFilterDefinitions,
    filterSets: FilterSetDefinitions,
  })
  .describe('FilterSetDefinitionsWithDefaultFilters');

export type FilterSetDefinitionsWithDefaultFilters = z.infer<
  typeof FilterSetDefinitionsWithDefaultFilters
>;

/**
 * For passing filters to the backend using the CustomViz extras
 */
export const FilterDefinitionForExtras = z
  .object({
    columnHeaderValue: z.string(),
    values: z.string().array(),
  })
  .describe('FilterDefinitionForExtras');

export type FilterDefinitionForExtras = z.infer<
  typeof FilterDefinitionForExtras
>;

export const FilterDefinitionForExtrasArray = z
  .array(FilterDefinitionForExtras)
  .describe('FilterDefinitionForExtrasArray');

export type FilterDefinitionForExtrasArray = z.infer<
  typeof FilterDefinitionForExtrasArray
>;
